import { EmbudoFrecuencia } from "./components/EmbudoFrecuencia";
import { EMBUDO_EJEMPLO } from "./datos/ejemplo";

/**
 * Insights: lecturas sobre los números de la marca. El embudo de frecuencia es
 * el centro — el resto son los anuncios que quedan fuera de él (por qué) y una
 * grilla de qué formatos rinden mejor.
 *
 * Es la primera pestaña de "Análisis de datos" (`AnalisisDatosPage`), no una
 * pantalla suelta: el padding de pantalla, el banner de vista de ejemplo y el
 * título los pone el contenedor. Acá solo queda el contenido del panel, con
 * "Últimos 30 días" como el recorte temporal que aplica a estos números (no al
 * de la otra pestaña, por eso no subió al encabezado común).
 */

interface ItemFueraDeEmbudo {
  nombre: string;
  nota: string;
}

const claseTarjetaFila =
  "flex items-center justify-between gap-3 rounded-2xl border border-[rgb(var(--brand-black-rgb)_/_0.08)] bg-white py-2.5 px-3.5";

function GrupoFueraDeEmbudo({
  titulo,
  explicacion,
  items,
}: {
  titulo: string;
  explicacion: string;
  items: ItemFueraDeEmbudo[];
}) {
  if (items.length === 0) return null;
  return (
    <div className="flex-1">
      <h3 className="text-[13px] font-semibold text-brand-black">{titulo}</h3>
      <p className="mt-0.5 text-[12px] text-[rgb(var(--brand-black-rgb)_/_0.55)]">{explicacion}</p>
      <div className="mt-3 flex flex-col gap-1.5">
        {items.map((item) => (
          <div key={item.nombre} className={claseTarjetaFila}>
            <span className="text-[12px] font-medium text-brand-black">{item.nombre}</span>
            <span className="text-[11px] text-[rgb(var(--brand-black-rgb)_/_0.5)]">{item.nota}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

interface ItemMetrica {
  nombre: string;
  valor: string;
  /** Solo `guardados` y `retencion` lo tienen; `sin_contenido` no — no hay
   *  contra qué ser proporcional ahí, así que esas tarjetas van sin barra. */
  peso?: number;
}

function TarjetaMetrica({ item }: { item: ItemMetrica }) {
  const tienePeso = typeof item.peso === "number" && Number.isFinite(item.peso);
  const anchoBarra = tienePeso ? `${Math.min(Math.max(item.peso!, 0), 1) * 100}%` : "0%";

  return (
    <div className="rounded-2xl border border-[rgb(var(--brand-black-rgb)_/_0.08)] bg-white p-3.5">
      <p className="text-[12px] text-[rgb(var(--brand-black-rgb)_/_0.55)]">{item.nombre}</p>
      <p className="mt-0.5 text-[16px] font-semibold text-brand-black">{item.valor}</p>
      {tienePeso && (
        <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-[rgb(var(--brand-black-rgb)_/_0.06)]">
          <div className="h-full rounded-full bg-brand-primary" style={{ width: anchoBarra }} />
        </div>
      )}
    </div>
  );
}

function GrupoMetricas({ titulo, items }: { titulo: string; items: ItemMetrica[] }) {
  return (
    <div>
      <h3 className="text-[13px] font-semibold text-brand-black">{titulo}</h3>
      <div className="mt-3 flex flex-col gap-2.5">
        {items.map((item) => (
          <TarjetaMetrica key={item.nombre} item={item} />
        ))}
      </div>
    </div>
  );
}

export const InsightsPage = () => (
  <div>
    <p className="text-[13px] text-[rgb(var(--brand-black-rgb)_/_0.55)]">Últimos 30 días</p>

    <div className="mt-4 rounded-2xl border border-[rgb(var(--brand-black-rgb)_/_0.08)] bg-white p-5">
      <h2 className="text-[14px] font-semibold text-brand-black">Embudo de frecuencia</h2>
      <p className="mt-0.5 text-[12px] text-[rgb(var(--brand-black-rgb)_/_0.55)]">
        El ancho de cada banda es cuánto se gastó en esa etapa — no un ancho decorativo.
      </p>
      <div className="mt-5">
        <EmbudoFrecuencia bandas={EMBUDO_EJEMPLO.bandas} />
      </div>
    </div>

    <div className="mt-8 flex gap-8">
      <GrupoFueraDeEmbudo
        titulo="Sin asentar"
        explicacion="Corren hace pocos días: todavía no hay señal suficiente para ubicarlos en una banda."
        items={EMBUDO_EJEMPLO.sin_asentar}
      />
      <div className="w-px bg-[rgb(var(--brand-black-rgb)_/_0.08)]" />
      <GrupoFueraDeEmbudo
        titulo="No suma"
        explicacion="Compiten por la misma audiencia que otro anuncio ya activo: no aportan alcance nuevo."
        items={EMBUDO_EJEMPLO.no_suma}
      />
    </div>

    <div className="mt-8 grid grid-cols-3 gap-6">
      <GrupoMetricas titulo="Qué se guarda más" items={EMBUDO_EJEMPLO.tarjetas.guardados} />
      <GrupoMetricas titulo="Qué retiene más" items={EMBUDO_EJEMPLO.tarjetas.retencion} />
      <GrupoMetricas titulo="Sin contenido nuevo hace más tiempo" items={EMBUDO_EJEMPLO.tarjetas.sin_contenido} />
    </div>
  </div>
);

export default InsightsPage;
